import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { TOOLBAR_CONTROL_WIDTH_PX } from './constants/ui';

// Suppress "Script error" messages globally before React renders
// These errors are often harmless and come from external scripts (like Chart.js CDN)
if (typeof window !== 'undefined') {
  const isScriptError = (message) => typeof message === 'string' && (
    message === 'Script error.' || message === 'Script error' || message.includes('Script error')
  );
  // Benign browser notice: a ResizeObserver callback resized something and the
  // browser skipped a delivery round. Nothing is broken, but the dev overlay
  // treats any window error as fatal and blanks the app. Observers still defer
  // their own layout writes (see EmailHtmlFrame); this only hides the notice.
  const isResizeObserverNotice = (message) => typeof message === 'string' && (
    message.includes('ResizeObserver loop completed with undelivered notifications')
    || message.includes('ResizeObserver loop limit exceeded')
  );
  const isBenignWindowError = (message) => isScriptError(message) || isResizeObserverNotice(message);
  // Intercept ALL error events before React's error overlay can catch them
  const originalErrorHandler = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Suppress generic "Script error" messages and the benign ResizeObserver notice
    if (isBenignWindowError(message)) {
      return true; // Suppress the error completely
    }
    // Call original handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // Suppress unhandled promise rejections related to script errors
  const rejectionHandler = function(event) {
    const reason = event.reason;
    const reasonStr = typeof reason === 'string' ? reason : reason?.message || reason?.toString() || '';
    if (isBenignWindowError(reasonStr)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  };
  window.addEventListener('unhandledrejection', rejectionHandler, true);

  // Intercept React error overlay's error handler
  if (process.env.NODE_ENV === 'development') {
    // Override console.error to catch errors before React's error overlay
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const message = args.join(' ');
      if (isBenignWindowError(message)) {
        // Suppress script error / ResizeObserver notice console messages
        return;
      }
      return originalConsoleError.apply(console, args);
    };

    // Try to intercept React error overlay after it loads
    const originalAddEventListener = window.addEventListener;
    window.addEventListener = function(type, listener, options) {
      if (type === 'error') {
        const wrappedListener = function(event) {
          const message = event.message || event.error?.message || String(event) || '';
          if (isBenignWindowError(message)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            return false;
          }
          if (typeof listener === 'function') {
            return listener.call(this, event);
          }
        };
        return originalAddEventListener.call(this, type, wrappedListener, options);
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.style.setProperty('--toolbar-control-width', `${TOOLBAR_CONTROL_WIDTH_PX}px`);
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
